import React from 'react'
import axios from 'axios'
import { BsChevronDown, BsChevronUp, BsSearch } from 'react-icons/bs'
import { MdRefresh } from 'react-icons/md'
import { getRequestHeaders } from '../api'
import { baseUrl } from '../assets/Animations'
import { TEACHER_STATUS_OPTIONS, matchesTeacherStatusFilters } from '../teacherDatabaseFilters'
import { getTeacherImageUrl } from './teacherProfileUtils'

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const text = (value) => String(value || '').trim()
const comparable = (value) => text(value).toLowerCase()
const isManager = (userData) => userData?.PermissionLevel === 'manager' && userData?.IsAdmin

const teacherId = (teacher) => text(teacher?.TeacherID || teacher?.teacherId || teacher?.id || teacher?._id)

const normalizeTeacher = (teacher) => ({
  ...teacher,
  InstrumentList: Array.isArray(teacher?.InstrumentList) ? teacher.InstrumentList : [],
  TownList: Array.isArray(teacher?.TownList) ? teacher.TownList : [],
  AvailabilityDetails: teacher?.AvailabilityDetails && typeof teacher.AvailabilityDetails === 'object'
    ? teacher.AvailabilityDetails
    : {},
})

const uniqueOptions = (values) => {
  const seen = new Set()
  return values.reduce((options, option) => {
    const id = text(option?.id)
    const label = text(option?.label)
    if (!id || !label || seen.has(id.toLowerCase())) return options
    seen.add(id.toLowerCase())
    options.push({ id, label })
    return options
  }, []).sort((first, second) => first.label.localeCompare(second.label))
}

const FilterGroup = ({ label, options, selected, onToggle }) => (
  <div className='atharo-hub__directory-filter-group'>
    <span>{label}</span>
    <div>
      {options.map((option) => {
        const active = selected.includes(option.id)
        return (
          <button
            type='button'
            className={active ? 'is-active' : ''}
            key={option.id}
            onClick={() => onToggle(option.id)}
            aria-pressed={active}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  </div>
)

const MultiSelectDropdown = ({ label, options, selected, onToggle, open, onToggleOpen }) => (
  <div className='atharo-hub__directory-dropdown'>
    <button
      type='button'
      className={selected.length > 0 ? 'is-active' : ''}
      onClick={onToggleOpen}
      aria-expanded={open}
    >
      <span>{label}{selected.length > 0 ? ` (${selected.length})` : ''}</span>
      <BsChevronDown aria-hidden='true' />
    </button>
    {open && (
      <div className='atharo-hub__directory-dropdown-menu' role='group' aria-label={`${label} filters`}>
        {options.length === 0 && <span className='atharo-hub__directory-dropdown-empty'>No options available</span>}
        {options.map((option) => {
          const checked = selected.includes(option.id)
          return (
            <button type='button' key={option.id} onClick={() => onToggle(option.id)}>
              <input type='checkbox' checked={checked} readOnly tabIndex='-1' />
              <span>{option.label}</span>
            </button>
          )
        })}
      </div>
    )}
  </div>
)

const TeacherAvatar = ({ teacher }) => {
  const [imageFailed, setImageFailed] = React.useState(false)
  const image = getTeacherImageUrl(teacher)
  const initials = text(teacher?.Name || teacher?.name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')

  return (
    <span className='atharo-hub__directory-avatar'>
      {image && !imageFailed
        ? <img src={image} alt='' onError={() => setImageFailed(true)} />
        : <span>{initials || 'T'}</span>}
    </span>
  )
}

const TeacherDirectory = ({ userData, onSelectTeacher }) => {
  const [teachers, setTeachers] = React.useState([])
  const [schools, setSchools] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')
  const [filtersOpen, setFiltersOpen] = React.useState(false)
  const [openDropdown, setOpenDropdown] = React.useState('')
  const [query, setQuery] = React.useState('')
  const [filters, setFilters] = React.useState({
    status: [],
    leftSchool: false,
    days: [],
    instrument: [],
    town: [],
    school: [],
  })

  const loadTeachers = React.useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [activeResponse, leftResponse, schoolsResponse] = await Promise.all([
        axios.get(`${baseUrl}/teacher-database-active`, getRequestHeaders()),
        axios.get(`${baseUrl}/teacher-database-left`, getRequestHeaders()),
        axios.get(`${baseUrl}/schools`, getRequestHeaders()),
      ])
      const teachersById = new Map()
      ;[activeResponse.data, leftResponse.data].forEach((list) => {
        ;(Array.isArray(list) ? list : []).forEach((teacher) => {
          const normalized = normalizeTeacher(teacher)
          const id = teacherId(normalized) || comparable(normalized.Name || normalized.name)
          if (id) teachersById.set(id, normalized)
        })
      })
      setTeachers([...teachersById.values()])
      setSchools(uniqueOptions((Array.isArray(schoolsResponse.data) ? schoolsResponse.data : []).map((school) => ({
        id: text(school?.schoolAbbreviation).toLowerCase(),
        label: text(school?.name) || text(school?.schoolAbbreviation).toUpperCase(),
      }))))
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    loadTeachers()
  }, [loadTeachers])

  const options = React.useMemo(() => {
    const selectedSchools = isManager(userData)
      ? [comparable(userData?.School)]
      : filters.school
    const townSource = selectedSchools.length === 0
      ? teachers
      : teachers.filter((teacher) => selectedSchools.includes(comparable(teacher.School)))

    return {
      instruments: uniqueOptions(teachers.flatMap((teacher) => teacher.InstrumentList.map((instrument) => ({
      id: text(instrument?.id || instrument?.name || instrument?.instrument),
      label: text(instrument?.name || instrument?.instrument || instrument?.parentInstrument),
    })))),
      towns: uniqueOptions(townSource.flatMap((teacher) => teacher.TownList.map((town) => ({
      id: text(town?.id || town?.townName),
      label: text(town?.townName || town?.id),
    })))),
    }
  }, [filters.school, teachers, userData])

  const updateMultiFilter = (filterId, optionId) => {
    setFilters((current) => {
      const selected = current[filterId]
      return {
        ...current,
        ...(filterId === 'school' ? { town: [] } : {}),
        [filterId]: selected.includes(optionId)
          ? selected.filter((id) => id !== optionId)
          : [...selected, optionId],
      }
    })
  }

  const clearFilters = () => {
    setQuery('')
    setFilters({ status: [], leftSchool: false, days: [], instrument: [], town: [], school: [] })
    setOpenDropdown('')
  }

  const filteredTeachers = React.useMemo(() => {
    const normalizedQuery = comparable(query)
    const queryTerms = normalizedQuery.split(/\s+/).filter(Boolean)
    const managerSchool = comparable(userData?.School)

    return teachers.filter((teacher) => {
      const instruments = teacher.InstrumentList
      const towns = teacher.TownList
      const searchable = [
        teacher.Name,
        teacher.Email,
        teacher.School,
        teacher.Status,
        ...instruments.map((instrument) => instrument?.name || instrument?.instrument),
        ...towns.map((town) => town?.townName || town?.id),
      ].map(comparable).filter(Boolean)
      const queryMatches = queryTerms.length === 0 || queryTerms.every((term) => (
        searchable.some((value) => value.includes(term))
      ))
      const schoolMatches = isManager(userData)
        ? comparable(teacher.School) === managerSchool
        : filters.school.length === 0 || filters.school.includes(comparable(teacher.School))
      const instrumentMatches = filters.instrument.length === 0 || filters.instrument.every((selected) => (
        instruments.some((instrument) => text(instrument?.id || instrument?.name || instrument?.instrument) === selected)
      ))
      const townMatches = filters.town.length === 0 || filters.town.every((selected) => (
        towns.some((town) => text(town?.id || town?.townName) === selected)
      ))
      const dayMatches = filters.days.length === 0 || filters.days.some((day) => (
        Array.isArray(teacher.AvailabilityDetails[day])
        && teacher.AvailabilityDetails[day].length > 0
        && teacher.WantsMoreStudents
        && !['goon', 'summer-break', 'leaving', 'Left School'].includes(teacher.Status)
      ))
      return queryMatches
        && schoolMatches
        && instrumentMatches
        && townMatches
        && dayMatches
        && matchesTeacherStatusFilters(teacher, filters.status, filters.leftSchool)
    }).sort((first, second) => text(first.Name).localeCompare(text(second.Name)))
  }, [filters, query, teachers, userData])

  const activeFilters = filters.status.length
    + filters.days.length
    + filters.instrument.length
    + filters.town.length
    + filters.school.length
    + (filters.leftSchool ? 1 : 0)

  return (
    <div className='atharo-hub__directory'>
      <label className='atharo-hub__search'>
        <BsSearch aria-hidden='true' />
        <span className='sr-only'>Search teachers</span>
        <input
          type='search'
          placeholder='Search teachers…'
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoComplete='off'
        />
      </label>

      <div className='atharo-hub__directory-toolbar'>
        <button type='button' onClick={() => setFiltersOpen((open) => !open)} aria-expanded={filtersOpen}>
          Filters{activeFilters > 0 ? ` (${activeFilters})` : ''}
          {filtersOpen ? <BsChevronUp aria-hidden='true' /> : <BsChevronDown aria-hidden='true' />}
        </button>
        {activeFilters > 0 && <button type='button' onClick={clearFilters}>Clear</button>}
        <span>{filteredTeachers.length} {filteredTeachers.length === 1 ? 'teacher' : 'teachers'}</span>
      </div>

      {filtersOpen && (
        <section className='atharo-hub__directory-filters' aria-label='Teacher database filters'>
          {!isManager(userData) && (
            <MultiSelectDropdown
              label='School'
              options={schools}
              selected={filters.school}
              onToggle={(id) => updateMultiFilter('school', id)}
              open={openDropdown === 'school'}
              onToggleOpen={() => setOpenDropdown((current) => current === 'school' ? '' : 'school')}
            />
          )}
          <MultiSelectDropdown
            label='Instrument'
            options={options.instruments}
            selected={filters.instrument}
            onToggle={(id) => updateMultiFilter('instrument', id)}
            open={openDropdown === 'instrument'}
            onToggleOpen={() => setOpenDropdown((current) => current === 'instrument' ? '' : 'instrument')}
          />
          <MultiSelectDropdown
            label='Town'
            options={options.towns}
            selected={filters.town}
            onToggle={(id) => updateMultiFilter('town', id)}
            open={openDropdown === 'town'}
            onToggleOpen={() => setOpenDropdown((current) => current === 'town' ? '' : 'town')}
          />
          <FilterGroup label='Days available' options={DAYS.map((day) => ({ id: day, label: day.slice(0, 3) }))} selected={filters.days} onToggle={(id) => updateMultiFilter('days', id)} />
          <FilterGroup label='Status' options={TEACHER_STATUS_OPTIONS} selected={filters.status} onToggle={(id) => updateMultiFilter('status', id)} />
          <label className='atharo-hub__directory-switch'>
            <input
              type='checkbox'
              checked={filters.leftSchool}
              onChange={(event) => setFilters((current) => ({ ...current, leftSchool: event.target.checked }))}
            />
            Show Left School
          </label>
        </section>
      )}

      {loading && <div className='atharo-hub__directory-state' role='status'>Loading teachers…</div>}
      {!loading && error && (
        <div className='atharo-hub__directory-state atharo-hub__directory-state--error' role='alert'>
          <span>Teacher data could not be loaded.</span>
          <button type='button' onClick={loadTeachers}><MdRefresh aria-hidden='true' /> Try again</button>
        </div>
      )}
      {!loading && !error && filteredTeachers.length === 0 && (
        <div className='atharo-hub__directory-state'>No teachers match these filters.</div>
      )}
      {!loading && !error && filteredTeachers.length > 0 && (
        <div className='atharo-hub__directory-results'>
          {filteredTeachers.map((teacher) => (
            <button type='button' key={teacherId(teacher) || teacher.Email} onClick={() => onSelectTeacher(teacher)}>
              <TeacherAvatar teacher={teacher} />
              <span>
                <strong>{teacher.Name || teacher.name}</strong>
                <small>{teacher.InstrumentList.map((instrument) => text(instrument?.name || instrument?.instrument)).filter(Boolean).join(', ') || 'No instruments listed'}</small>
              </span>
              <em className={text(teacher.Status) === 'Left School' ? 'is-old' : ''}>{teacher.Status || 'Active'}</em>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default TeacherDirectory
